import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import { useActivityImportsPurchasedGoodsCreate } from '@shared/api/lib/activity-imports/activity-imports';
import {
  getCompanyReportsActivitiesDirectGasEmissionsListQueryKey,
  getCompanyReportsActivitiesElectricityListQueryKey,
  getCompanyReportsActivitiesHeatListQueryKey,
  getCompanyReportsActivitiesMobileCombustionListQueryKey,
  getCompanyReportsActivitiesStationaryCombustionListQueryKey,
  getCompanyReportsImportsListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useActivityImportsDelete,
  useActivityImportsPartialUpdate,
  useActivityImportsProgressList,
  useCompanyReportsImportsList,
  useCompanyReportsImportsProcessDirectGasEmissionsCreate,
  useCompanyReportsImportsProcessElectricityCreate,
  useCompanyReportsImportsProcessHeatCreate,
  useCompanyReportsImportsProcessMobileCombustionCreate,
  useCompanyReportsImportsProcessStationaryCombustionCreate,
  useCompanyReportsImportsTemplateScope12Retrieve,
} from '@shared/api/lib/company-reports/company-reports';
import {
  getPurchasedGoodsFoodListQueryKey,
  getPurchasedGoodsPackagingListQueryKey,
} from '@shared/api/lib/purchased-goods/purchased-goods';
import {
  ActivityImportProgress,
  ActivityImportStatusEnum,
  CompanyReportsImportsListParams,
  ImportTypeEnum,
  PaginatedActivityImportListList,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

export const useDeleteImport = () => {
  const queryClient = useQueryClient();
  return useActivityImportsDelete({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () =>
        Promise.all([
          refreshQueries(queryClient, getCompanyReportsImportsListQueryKey()),
          refreshQueries(queryClient, getPurchasedGoodsFoodListQueryKey()),
          refreshQueries(queryClient, getPurchasedGoodsPackagingListQueryKey()),
        ]),
    },
  });
};

/**
 * Sends a purchased goods import to the catalogue pipeline as JSON. The
 * backend restricts the endpoint to staff until the pipeline replaces the
 * mixed-import upload; it answers 201 with no body and processes in the
 * background, so the imports list is refreshed to show the new import.
 */
export const useCreatePurchasedGoodsImport = () => {
  const queryClient = useQueryClient();
  return useActivityImportsPurchasedGoodsCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () =>
        refreshQueries(queryClient, getCompanyReportsImportsListQueryKey()),
    },
  });
};

export const useUpdateImport = () => {
  const queryClient = useQueryClient();
  return useActivityImportsPartialUpdate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () =>
        refreshQueries(queryClient, getCompanyReportsImportsListQueryKey()),
    },
  });
};

export const useGetImports = (
  params?: CompanyReportsImportsListParams,
  options?: { enabled?: boolean }
) => {
  const { enabled } = options || {};
  return useCompanyReportsImportsList(params, {
    request: useAxiosConfig(),
    query: { enabled: enabled !== false },
  });
};

const isInFlight = (status: ActivityImportProgress['status']): boolean =>
  status === ActivityImportStatusEnum.importing ||
  status === ActivityImportStatusEnum.processing;

/** Fast enough to catch an import finishing. */
export const ACTIVE_POLL_INTERVAL_MS = 3000;
/** Nothing is running, so this only has to notice one starting. */
export const IDLE_POLL_INTERVAL_MS = 30000;

/**
 * Polls the in-flight imports. Unpaginated and unfiltered by design: an
 * organisation has a couple of unfinished imports at most, so this is far
 * cheaper than re-fetching the imports list on a timer.
 *
 * The interval follows the payload rather than sitting at 3s: a 200k-row import
 * takes half an hour, so a running import's progress bar moves imperceptibly
 * between polls, and the vast majority of the time nothing is running at all.
 * Failed imports stay in the payload indefinitely, so this keys on the in-flight
 * statuses rather than on the payload being non-empty.
 */
export const useGetImportsProgress = (options?: { enabled?: boolean }) => {
  const { enabled } = options || {};
  return useActivityImportsProgressList({
    request: useAxiosConfig(),
    query: {
      refetchInterval: (data) =>
        data?.some((activityImport) => isInFlight(activityImport.status))
          ? ACTIVE_POLL_INTERVAL_MS
          : IDLE_POLL_INTERVAL_MS,
      // A progress bar nobody is looking at needs no polling; returning to the
      // tab refetches on focus.
      refetchIntervalInBackground: false,
      enabled: enabled !== false,
    },
  });
};

/**
 * Patches the polled progress onto a page of the imports list.
 *
 * The list itself is fetched on navigation only. While an import is in flight
 * its status and row counts come from the progress poll, and the moment it
 * drops out of that payload the list is invalidated once so the finished row
 * picks up its category breakdown.
 */
export const useImportsWithProgress = (
  data: PaginatedActivityImportListList | undefined
): PaginatedActivityImportListList | undefined => {
  const queryClient = useQueryClient();
  const { data: inFlightImports } = useGetImportsProgress();
  const previousUuids = React.useRef<string[]>([]);

  React.useEffect(() => {
    if (!inFlightImports) return;
    const uuids = inFlightImports.map((activityImport) => activityImport.uuid);
    const finished = previousUuids.current.filter(
      (uuid) => !uuids.includes(uuid)
    );
    previousUuids.current = uuids;
    if (finished.length > 0) {
      void queryClient.invalidateQueries(
        getCompanyReportsImportsListQueryKey()
      );
    }
  }, [inFlightImports, queryClient]);

  return React.useMemo(() => {
    if (!data || !inFlightImports?.length) return data;
    const progressByUuid = new Map(
      inFlightImports.map((activityImport) => [
        activityImport.uuid,
        activityImport,
      ])
    );

    return {
      ...data,
      results: data.results.map((row) => {
        const progress = progressByUuid.get(row.uuid);
        if (!progress) return row;
        return {
          ...row,
          status: progress.status,
          numberOfRows: progress.numberOfRows,
          numberOfProcessedRows: progress.numberOfProcessedRows,
          numberOfImportedRows: progress.numberOfImportedRows,
          numberOfFailedRows: progress.numberOfFailedRows,
        };
      }),
    };
  }, [data, inFlightImports]);
};

/**
 * A v2 import declares its contents through `activityTypes`; legacy imports
 * carry a single `activityType`.
 */
export const coversActivityType = (
  activityImport: ActivityImportProgress,
  activityType: 'food' | 'packaging'
): boolean =>
  activityImport.activityType === activityType ||
  (activityImport.activityTypes?.some((type) => type.slug === activityType) ??
    false);

/**
 * Whether an import of this type is still running. Shares the progress query
 * with the sidebar, so React Query dedupes it to one poll.
 */
export const useHasActiveImports = (
  importType: ImportTypeEnum,
  activityType: 'food' | 'packaging'
) => {
  const { data } = useGetImportsProgress();
  return (
    data
      ?.filter(
        (activityImport) =>
          activityImport.importType === importType &&
          coversActivityType(activityImport, activityType)
      )
      .some((activityImport) => isInFlight(activityImport.status)) ?? false
  );
};

/**
 * The Scope 1/2 importer submits multipart `FormData`, so the company report
 * uuid lives under the `company_report` form field rather than a JSON property.
 * Falls back to a JSON `companyReport` for non-multipart callers.
 */
const getCompanyReportUuid = (variables: {
  data: unknown;
}): string | undefined => {
  const { data } = variables;
  if (data instanceof FormData) {
    return (data.get('company_report') as string) || undefined;
  }
  return (
    (data as { companyReport?: string | null })?.companyReport ?? undefined
  );
};

/**
 * Scope 1 & 2 synchronous activity imports (electricity, heat, stationary
 * combustion, mobile combustion, direct gas emissions). Each wrapper invalidates
 * the matching activity list + the company report so the category page reflects
 * the newly imported activities.
 */
export const useProcessElectricityImport = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsImportsProcessElectricityCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) => {
        const reportUuid = getCompanyReportUuid(variables);
        if (!reportUuid) return;
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(reportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesElectricityListQueryKey(reportUuid)
        );
      },
    },
  });
};

export const useProcessHeatImport = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsImportsProcessHeatCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) => {
        const reportUuid = getCompanyReportUuid(variables);
        if (!reportUuid) return;
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(reportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesHeatListQueryKey(reportUuid)
        );
      },
    },
  });
};

export const useProcessStationaryCombustionImport = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsImportsProcessStationaryCombustionCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) => {
        const reportUuid = getCompanyReportUuid(variables);
        if (!reportUuid) return;
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(reportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesStationaryCombustionListQueryKey(
            reportUuid
          )
        );
      },
    },
  });
};

export const useProcessMobileCombustionImport = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsImportsProcessMobileCombustionCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) => {
        const reportUuid = getCompanyReportUuid(variables);
        if (!reportUuid) return;
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(reportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesMobileCombustionListQueryKey(reportUuid)
        );
      },
    },
  });
};

export const useProcessDirectGasEmissionsImport = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsImportsProcessDirectGasEmissionsCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_data, variables) => {
        const reportUuid = getCompanyReportUuid(variables);
        if (!reportUuid) return;
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(reportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesDirectGasEmissionsListQueryKey(reportUuid)
        );
      },
    },
  });
};

/**
 * Downloads the dynamically generated Scope 1 & 2 data-collection template,
 * pre-populated with the organisation's sites, time periods, fuels and gases.
 * Disabled by default — trigger the download on demand via `refetch()`, then
 * save the returned blob. Mirrors `useDownloadReportResults`.
 */
export const useDownloadScope1And2Template = ({ enabled = false } = {}) => {
  // The app's axios config defaults `responseType: 'json'`, and the generated
  // client spreads these request options last — so we must force `blob` here,
  // otherwise axios parses the .xlsx binary as JSON and the download breaks.
  const axiosConfig = useAxiosConfig();
  return useCompanyReportsImportsTemplateScope12Retrieve({
    request: { ...axiosConfig, responseType: 'blob' },
    query: {
      enabled,
      cacheTime: 0,
      staleTime: 0,
    },
  });
};
